import React__default from 'react';
import FormItemInput from './FormItemInput.es.js';
import FormItemLabel from './FormItemLabel.es.js';
import FieldContext from 'rc-field-form/lib/FieldContext';
import { Field } from 'rc-field-form';
import { logger } from '@teko/log';
import { toArray, omit, mergeCls } from '../../helpers/utils.es.js';
import styled from '@emotion/styled';
import { Row } from '../Grid/Row.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function hasValidName(name) {
  if (name === null) {
    logger.info(false, "Form.Item", "`null` is passed as `name` property");
  }
  return !(name === void 0 || name === null);
}
function genEmptyMeta() {
  return {
    errors: [],
    warnings: [],
    touched: false,
    validating: false,
    name: []
  };
}
function FormItem(props) {
  const _a = props, {
    name,
    noStyle,
    dependencies,
    style,
    className = "",
    shouldUpdate,
    rules,
    children,
    required,
    label,
    messageVariables,
    trigger = "onChange",
    validateTrigger,
    hidden
  } = _a, restProps = __objRest(_a, [
    "name",
    "noStyle",
    "dependencies",
    "style",
    "className",
    "shouldUpdate",
    "rules",
    "children",
    "required",
    "label",
    "messageVariables",
    "trigger",
    "validateTrigger",
    "hidden"
  ]);
  const isRenderProps = typeof children === "function";
  const { validateTrigger: contextValidateTrigger } = React__default.useContext(FieldContext);
  const mergedValidateTrigger = validateTrigger !== void 0 ? validateTrigger : contextValidateTrigger;
  const hasName = hasValidName(name);
  const [meta, setMeta] = React__default.useState(() => genEmptyMeta());
  const onMetaChange = (nextMeta) => {
    setMeta(nextMeta.destroy ? genEmptyMeta() : nextMeta);
  };
  const [mergedErrors, mergedWarnings] = React__default.useMemo(() => {
    const errorList = [...meta.errors];
    const warningList = [...meta.warnings];
    return [errorList, warningList];
  }, [meta.errors, meta.warnings]);
  function renderLayout(baseChildren, fieldId, isRequired) {
    if (noStyle && !hidden) {
      return baseChildren;
    }
    const hasHelp = mergedErrors.length || mergedWarnings.length;
    return /* @__PURE__ */ React__default.createElement(StyledRow, __spreadValues({
      className: mergeCls(className, "teko-form-item", hasHelp ? "teko-form-item-with-help" : ""),
      style,
      key: "row"
    }, omit(restProps, [
      "colon",
      "extra",
      "getValueFromEvent",
      "getValueProps",
      "htmlFor",
      "initialValue",
      "isListField",
      "labelAlign",
      "labelCol",
      "normalize",
      "preserve",
      "validateFirst",
      "valuePropName",
      "wrapperCol",
      "_internalItemRender"
    ])), /* @__PURE__ */ React__default.createElement(FormItemLabel, __spreadValues({
      prefixCls: "teko-form",
      htmlFor: fieldId,
      required: isRequired
    }, props)), /* @__PURE__ */ React__default.createElement(FormItemInput, __spreadProps(__spreadValues(__spreadValues({}, props), meta), {
      errors: mergedErrors,
      warnings: mergedWarnings,
      prefixCls: "teko-form"
    }), baseChildren));
  }
  if (!hasName && !isRenderProps && !dependencies) {
    return renderLayout(children);
  }
  let variables = {};
  if (typeof label === "string") {
    variables.label = label;
  } else if (name) {
    variables.label = String(name);
  }
  if (messageVariables) {
    variables = __spreadValues(__spreadValues({}, variables), messageVariables);
  }
  return /* @__PURE__ */ React__default.createElement(Field, __spreadProps(__spreadValues({}, props), {
    messageVariables: variables,
    trigger,
    validateTrigger: mergedValidateTrigger,
    onMetaChange
  }), (control, renderMeta, context) => {
    const mergedName = !!name && !!renderMeta ? renderMeta.name : [];
    const fieldId = mergedName.length ? mergedName.join("_") : void 0;
    const isRequired = required !== void 0 ? required : !!(rules && rules.some((rule) => {
      if (rule && typeof rule === "object" && rule.required && !rule.warningOnly) {
        return true;
      }
      if (typeof rule === "function") {
        const ruleEntity = rule(context);
        return ruleEntity && ruleEntity.required && !ruleEntity.warningOnly;
      }
      return false;
    }));
    const mergedControl = __spreadValues({}, control);
    let childNode = null;
    if (Array.isArray(children) && hasName) {
      childNode = children;
    } else if (React__default.isValidElement(children)) {
      const childProps = __spreadValues(__spreadValues({}, children.props), mergedControl);
      if (!childProps.id) {
        childProps.id = fieldId;
      }
      const triggers = /* @__PURE__ */ new Set([
        ...toArray(trigger),
        ...toArray(mergedValidateTrigger)
      ]);
      triggers.forEach((eventName) => {
        childProps[eventName] = (...args) => {
          var _a2, _b, _c;
          (_a2 = mergedControl[eventName]) == null ? void 0 : _a2.call(mergedControl, ...args);
          (_c = (_b = children.props)[eventName]) == null ? void 0 : _c.call(_b, ...args);
        };
      });
      childNode = React__default.cloneElement(children, childProps);
    } else if (isRenderProps && (shouldUpdate || dependencies) && !hasName) {
      childNode = children(context);
    } else {
      childNode = children;
    }
    return renderLayout(childNode, fieldId, isRequired);
  });
}
const StyledRow = styled(Row)`
  margin-bottom: 16px;
  &.teko-form-item-with-help {
    margin-bottom: 0;
  }
`;

export { FormItem as default };
